import { motion } from "framer-motion";
import {
    AlertTriangle,
    BadgeCheck,
    Briefcase,
    CheckCircle2,
    Cloud,
    Flame,
    Loader2,
    RefreshCw,
    Sparkles,
    Users,
    Zap,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../lib/api";

const PRESET_LIMITS = [5, 25, 50, 100, 200];

export default function IntegrationsLayan() {
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [statusError, setStatusError] = useState(null);
    const [limit, setLimit] = useState(50);
    const [scoreWithAi, setScoreWithAi] = useState(true);
    const [offerScope, setOfferScope] = useState("active"); // active | all | archived
    const [syncing, setSyncing] = useState(false);
    const [lastSync, setLastSync] = useState(null);

    const loadStatus = useCallback(async (withApplicants = false) => {
        setStatusLoading(true);
        try {
            const { data } = await api.get(
                `/integrations/layan/status${withApplicants ? "?include_applicants=true" : ""}`,
            );
            setStatus(data);
            setStatusError(null);
        } catch (e) {
            setStatusError(e.response?.data?.detail || "Impossible de joindre Layan");
        } finally {
            setStatusLoading(false);
        }
    }, []);

    useEffect(() => {
        // Fast first probe, then a deeper one to fetch the applicants count
        loadStatus(false).then(() => loadStatus(true));
    }, [loadStatus]);

    async function runSync() {
        setSyncing(true);
        const startedAt = Date.now();
        toast.info(`Synchronisation Layan lancée (${limit} candidats)`);
        try {
            const scopeMap = {
                active: "Publiée",
                all: "Publiée,Brouillon,Archivée",
                archived: "Archivée",
            };
            const offerStatusesParam = scopeMap[offerScope] || "Publiée";
            const { data } = await api.post(
                `/integrations/layan/sync?limit=${limit}&score_with_ai=${scoreWithAi}&offer_statuses=${encodeURIComponent(offerStatusesParam)}`,
            );
            setLastSync(data);
            const seconds = Math.round((Date.now() - startedAt) / 1000);
            toast.success(`Sync terminée : ${data.created} créés en ${seconds}s`);
            loadStatus(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de la synchronisation");
        } finally {
            setSyncing(false);
        }
    }

    async function runDevouvoyer() {
        if (!window.confirm("Ré-appliquer le vouvoiement sur tous les candidats existants ? L'opération est instantanée et sans coût IA.")) return;
        try {
            const { data } = await api.post("/integrations/layan/devouvoyer-all");
            toast.success(`Vouvoiement appliqué : ${data.updated} candidat(s) corrigé(s) sur ${data.scanned} analysés`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    const connected = status?.connected;

    return (
        <div className="space-y-6" data-testid="integrations-layan">
            <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                    <Cloud className="h-3 w-3" /> Intégration ATS
                </div>
                <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                    Synchronisation Layan
                </h1>
                <p className="mt-1 text-sm font-medium text-slate-500">
                    Importez automatiquement vos candidatures Layan et déclenchez l'analyse IA.
                </p>
            </div>

            <div className="grid lg:grid-cols-[1fr_360px] gap-6">
                {/* Left : status + run */}
                <div className="space-y-6">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card-soft p-6 md:p-7"
                        data-testid="integration-status"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
                                    Statut de la connexion
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                    {statusLoading && !status ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                            <span className="text-2xl font-extrabold text-slate-400">
                                                Vérification…
                                            </span>
                                        </>
                                    ) : connected ? (
                                        <>
                                            <span className="h-2 w-2 rounded-full bg-shariti-green animate-pulse" />
                                            <span className="text-2xl font-extrabold text-shariti-green">
                                                Connecté
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="h-2 w-2 rounded-full bg-shariti-red" />
                                            <span className="text-2xl font-extrabold text-shariti-red">
                                                Déconnecté
                                            </span>
                                        </>
                                    )}
                                </div>
                                {status?.base && (
                                    <p className="mt-1 text-xs font-medium text-slate-500">
                                        {status.base} · Société {status.company_id || "—"}
                                    </p>
                                )}
                                {statusError && (
                                    <p className="mt-2 text-xs font-bold text-shariti-red">
                                        {statusError}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => loadStatus(true)}
                                data-testid="status-refresh"
                                className="btn-ghost"
                                title="Rafraîchir le statut"
                            >
                                <RefreshCw className={`h-4 w-4 ${statusLoading ? "animate-spin" : ""}`} />
                            </button>
                        </div>

                        {connected && (
                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <StatBox
                                    icon={Users}
                                    label="Candidats Layan"
                                    value={status.applicants_count ?? "…"}
                                    tone="orange"
                                />
                                <StatBox
                                    icon={Briefcase}
                                    label="Offres actives"
                                    value={status.offers_count}
                                    tone="dark"
                                />
                            </div>
                        )}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="card-soft p-6 md:p-7"
                        data-testid="integration-runner"
                    >
                        <h3 className="text-lg font-extrabold tracking-tight">Lancer une synchronisation</h3>
                        <p className="mt-1 text-sm font-medium text-slate-500">
                            Les candidats déjà importés sont ignorés. L'IA analyse uniquement les nouveaux.
                        </p>

                        <div className="mt-6 space-y-5">
                            <div>
                                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                                    Périmètre des offres
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        { key: "active", label: "Offres publiées uniquement", hint: "Recommandé" },
                                        { key: "all", label: "Toutes les offres" },
                                        { key: "archived", label: "Offres archivées" },
                                    ].map((s) => (
                                        <button
                                            key={s.key}
                                            data-testid={`scope-${s.key}`}
                                            onClick={() => setOfferScope(s.key)}
                                            className={`h-9 px-4 rounded-full text-xs font-bold border transition ${
                                                offerScope === s.key
                                                    ? "bg-shariti-orange text-white border-shariti-orange"
                                                    : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                                            }`}
                                        >
                                            {s.label}
                                            {s.hint && offerScope === s.key && (
                                                <span className="ml-1.5 text-[9px] opacity-80">({s.hint})</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-2 text-[11px] font-medium text-slate-400">
                                    Seuls les candidats reliés à une offre du périmètre sélectionné seront importés.
                                </p>
                            </div>

                            <div>
                                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                                    Nombre de candidats à importer
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {PRESET_LIMITS.map((n) => (
                                        <button
                                            key={n}
                                            data-testid={`limit-${n}`}
                                            onClick={() => setLimit(n)}
                                            className={`h-9 px-4 rounded-full text-xs font-bold border transition ${
                                                limit === n
                                                    ? "bg-shariti-orange text-white border-shariti-orange"
                                                    : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                                            }`}
                                        >
                                            {n} candidats
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-2 text-[11px] font-medium text-slate-400">
                                    Estimation : ~{Math.round((scoreWithAi ? 17 : 1) * limit)} s · ~{limit} appels Claude {scoreWithAi ? "(IA activée)" : "(IA désactivée)"}
                                </p>
                            </div>

                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={scoreWithAi}
                                    onChange={(e) => setScoreWithAi(e.target.checked)}
                                    data-testid="score-with-ai"
                                    className="h-4 w-4 rounded border-shariti-gray accent-shariti-orange"
                                />
                                <span className="text-sm font-bold text-shariti-black">
                                    Analyser avec Claude IA (recommandé)
                                </span>
                            </label>

                            <button
                                onClick={runSync}
                                disabled={syncing || !connected}
                                data-testid="run-sync"
                                className="btn-shariti w-full md:w-auto"
                            >
                                {syncing ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Synchronisation en cours…
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="h-4 w-4" />
                                        Lancer la synchronisation
                                    </>
                                )}
                            </button>
                            {syncing && (
                                <div className="text-xs font-semibold text-slate-500 italic">
                                    Cette opération peut prendre plusieurs minutes selon le volume et l'IA. Vous pouvez quitter cette page, la sync continue.
                                </div>
                            )}
                            <div className="pt-3 mt-3 border-t border-shariti-gray">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                                    Maintenance du vouvoiement
                                </div>
                                <button
                                    type="button"
                                    onClick={runDevouvoyer}
                                    data-testid="run-devouvoyer"
                                    className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange hover:border-shariti-orange px-4 h-9 text-xs font-extrabold transition"
                                >
                                    Ré-appliquer le « vous » sur tous les candidats
                                </button>
                                <p className="mt-1.5 text-[10px] font-medium text-slate-400 max-w-md">
                                    Corrige mécaniquement les « tu » résiduels dans les analyses IA déjà importées. Instantané, sans coût.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {lastSync && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="card-soft p-6 md:p-7"
                            data-testid="integration-result"
                        >
                            <div className="flex items-center gap-2 mb-3">
                                <CheckCircle2 className="h-5 w-5 text-shariti-green" />
                                <h3 className="text-lg font-extrabold tracking-tight">
                                    Dernière synchronisation
                                </h3>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <ResultStat label="Importés" value={lastSync.created} tone="orange" />
                                <ResultStat label="Ignorés (déjà là)" value={lastSync.skipped_existing} tone="muted" />
                                <ResultStat label="Pépites détectées" value={lastSync.pepites} tone="yellow" />
                                <ResultStat label="Durée" value={`${lastSync.duration_s}s`} tone="muted" />
                            </div>
                            {lastSync.sample?.length > 0 && (
                                <div className="mt-5">
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                                        Aperçu des candidats importés
                                    </div>
                                    <div className="space-y-1">
                                        {lastSync.sample.map((s) => (
                                            <Link
                                                key={s.id}
                                                to={`/candidats/${s.id}`}
                                                className="flex items-center justify-between gap-3 p-3 rounded-2xl hover:bg-shariti-gray transition"
                                            >
                                                <div className="min-w-0">
                                                    <div className="text-sm font-extrabold text-shariti-black truncate">
                                                        {s.name}
                                                    </div>
                                                    <div className="text-xs font-medium text-slate-500 truncate">
                                                        {s.city} · Layan #{s.layan_id}
                                                    </div>
                                                </div>
                                                <span
                                                    className={`text-sm font-black ${
                                                        (s.ai_score || 0) >= 82
                                                            ? "text-shariti-orange"
                                                            : "text-slate-500"
                                                    }`}
                                                >
                                                    {s.ai_score || "—"}
                                                </span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}
                </div>

                {/* Right : help / what's next */}
                <aside className="space-y-4">
                    <div className="card-soft p-5">
                        <div className="flex items-center gap-2 text-shariti-orange">
                            <Sparkles className="h-4 w-4" />
                            <h4 className="text-sm font-extrabold uppercase tracking-widest">Comment ça marche</h4>
                        </div>
                        <ul className="mt-3 space-y-2.5 text-xs font-medium text-slate-600">
                            <li className="flex gap-2">
                                <span className="text-shariti-orange font-extrabold">1.</span>
                                PREREC interroge l'API Layan via votre clé sécurisée.
                            </li>
                            <li className="flex gap-2">
                                <span className="text-shariti-orange font-extrabold">2.</span>
                                Chaque nouvelle candidature est importée (dédoublonnée par <code className="font-mono text-[10px] bg-shariti-cream px-1 py-0.5 rounded">layan_id</code>).
                            </li>
                            <li className="flex gap-2">
                                <span className="text-shariti-orange font-extrabold">3.</span>
                                Claude Sonnet 4.5 analyse le CV et attribue un score, des badges et une recommandation.
                            </li>
                            <li className="flex gap-2">
                                <span className="text-shariti-orange font-extrabold">4.</span>
                                Les pépites (score ≥ 82) notifient automatiquement vos pré-recruteurs.
                            </li>
                        </ul>
                    </div>
                    <div className="card-soft p-5 bg-shariti-orange-soft border-shariti-orange/30">
                        <div className="flex items-center gap-2 text-shariti-orange">
                            <Zap className="h-4 w-4" />
                            <h4 className="text-sm font-extrabold uppercase tracking-widest">Prochaine étape</h4>
                        </div>
                        <p className="mt-2 text-xs font-semibold text-slate-700 leading-relaxed">
                            Webhook temps réel : Layan peut pousser chaque nouvelle candidature à PREREC pour une analyse instantanée, sans bouton à cliquer. Activez-le côté Layan lorsque vous êtes prêt(e).
                        </p>
                    </div>
                    <div className="card-soft p-5">
                        <div className="flex items-center gap-2 text-shariti-black">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            <h4 className="text-sm font-extrabold uppercase tracking-widest">À savoir</h4>
                        </div>
                        <ul className="mt-3 space-y-1.5 text-xs font-medium text-slate-600">
                            <li>· Le scoring IA prend ~15s par candidat.</li>
                            <li>· La clé Layan est stockée chiffrée côté serveur.</li>
                            <li>· Les candidats refusés Layan ne sont pas réimportés.</li>
                        </ul>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function StatBox({ icon: Icon, label, value, tone = "orange" }) {
    const styles =
        tone === "dark"
            ? "bg-shariti-black text-white"
            : "bg-shariti-orange-soft text-shariti-orange";
    return (
        <div className={`rounded-2xl p-4 ${styles}`}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest opacity-80">
                    {label}
                </span>
                <Icon className="h-4 w-4 opacity-80" strokeWidth={2.4} />
            </div>
            <div className="mt-1 text-3xl font-black tracking-tight">{value ?? "—"}</div>
        </div>
    );
}

function ResultStat({ label, value, tone }) {
    const colors = {
        orange: "text-shariti-orange",
        yellow: "text-amber-600",
        muted: "text-slate-600",
    };
    return (
        <div className="rounded-2xl bg-shariti-cream p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {label}
            </div>
            <div className={`mt-1 text-2xl font-black tracking-tight ${colors[tone] || colors.muted}`}>
                {value}
            </div>
        </div>
    );
}
